import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { CompanyNav } from "./CompanyNav";
import { CompanyTestsList } from "./CompanyTestsList";
import { useOrganizationAccess } from "@/hooks/useOrganizationAccess";
import { ActiveTestsBanner } from "@/components/shared/ActiveTestsBanner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import type { CompanyPortalConfig } from "@/config/companies";

interface CompanyAppPageProps {
  config: CompanyPortalConfig;
}

export function CompanyAppPage({ config }: CompanyAppPageProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Check if user has access to this organization
  const { data: accessCheck, isLoading: checkingAccess } = useOrganizationAccess(
    config.organizationId
  );

  // Apply company branding gradient
  const gradientStyle = {
    background: `linear-gradient(to bottom right, ${config.branding.gradientStart}, ${config.branding.gradientEnd})`,
  };

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!user) {
      navigate(config.routes.login);
    }
  }, [user, navigate, config.routes.login]);

  if (!user) {
    return null;
  }

  // Show loading state while checking access
  if (checkingAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Show access denied if user doesn't belong to organization
  if (!accessCheck?.hasAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md w-full space-y-4">
          <Alert variant="destructive">
            <AlertDescription>
              You don't have access to this organization's portal. 
              Please contact the organization administrator if you believe this is an error.
            </AlertDescription>
          </Alert>
          <Button 
            onClick={() => navigate('/')} 
            className="w-full"
            variant="outline"
          >
            Return to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full" style={gradientStyle}>
      <CompanyNav config={config} />
      <ActiveTestsBanner />
      
      <main className="pt-24 px-6 pb-20 max-w-7xl mx-auto w-full bg-purple-50">
        {/* Welcome Section */}
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-3 tracking-tight">
            Welcome to {config.name}
          </h1>
          <p className="text-lg text-muted-foreground">
            Explore available tests and showcase your skills
          </p>
        </div>

        {/* Tests List */}
        <CompanyTestsList config={config} />
      </main>

      {/* Footer - Powered by Testera - Fixed at bottom */}
      <div className="fixed bottom-0 left-0 right-0 border-t border-border bg-background/95 backdrop-blur-sm z-10">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <p className="text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
            Powered by <img src="/lovable-uploads/e7e0cd62-757f-4e57-b3bf-42cffdc8d843.png" alt="Testera" className="h-4 w-auto" /> <span className="font-medium text-foreground">Testera</span>
          </p>
        </div>
      </div>
    </div>
  );
}
